/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import _ from 'underscore';
import React from 'react';
import datasetTypes from '../../../enums/datasetTypes';
import {
    IMultiAxisDataServiceDataLayoutMemberExpansion,
    IMultiAxisDataset,
    IMultiAxisDatasetNode,
} from '../../../store/types/MultiAxisDatasetTypes';
import visitMultiAxisDatasetNode from '../../../store/util/visitMultiAxisDatasetNode';
import {
    ICrosstabVisualization,
    IInternalCrosstabData,
    IInternalCrosstabDataRow,
    IInternalCrosstabHeaderRow,
    OnScrollParameter
} from './types/CrosstabVisualizationTypes';
import {
    IAxisItem, ILinkToElemPair, TAxis
} from '../../../store/types/StateTypes';
import CrosstabProbeLayoutManager from './parts/CrosstabProbeLayoutManager';
import CrosstabAdaptiveSizeLayoutManager from './parts/CrosstabAdaptiveSizeLayoutManager';
import rangeVisitor from './util/RangeVisitor';
import positionVisitor from './util/PositionVisitor';
import { IComponentsRefsRegister, ILinkToRefObjectPair } from '../types/commonTypes';
import missingIndexVisitor from './util/MissingIndexVisitor';
import visitMultiAxisDatasetNodeNoTotals from '../../../store/util/visitMultiAxisDatasetNodeNoTotals';
import crosstabVisualizationStaticPropertiesReducer from './reducers/crosstabVisualizationStaticPropertiesReducer';
import getMultiAxisDatasetNode from '../../../store/util/getMultiAxisDatasetNode';

function readHeader(root: IMultiAxisDatasetNode, levels: TAxis, from: number, to: number, summary: any, empty: boolean, sizeResolver: (nodeIndex: number) => number) {

    const context = {
        array: [],
        stack: [],
        representationCache: new Map(),
        axisSize: summary.items.length,
        visitedSize: 0,
        visitedCount: 0,
        complete: summary.complete,
        empty,
        sizeResolver,
        from,
        to
    };

    let { array, visitedSize, loadingRequired } = visitMultiAxisDatasetNodeNoTotals(
        root,
        rangeVisitor,
        context,
        levels.map((level) => ((level as IAxisItem).includeAll))
    );

    if (!array.length) {
        const result = visitMultiAxisDatasetNode(
            root,
            rangeVisitor,
            context,
            true
        );
        // eslint-disable-next-line prefer-destructuring
        array = result.array;
        // eslint-disable-next-line prefer-destructuring
        visitedSize = result.visitedSize;
        // eslint-disable-next-line prefer-destructuring
        loadingRequired = result.loadingRequired;
    }

    let missingIndex = -1;
    if (loadingRequired) {
        // eslint-disable-next-line prefer-destructuring
        missingIndex = visitMultiAxisDatasetNode(root, missingIndexVisitor, {
            indexStack: [],
            currentIndex: -1,
            missingIndex: -1
        }).missingIndex;
    }

    return { array, visitedSize, missingIndex }
}

function readAggregations(datasetAggregations: Array<Array<any>>, columns: IInternalCrosstabHeaderRow[], rows: IInternalCrosstabHeaderRow[]) {
    const data: IInternalCrosstabDataRow[] = [];

    for (let i = 0; i < rows.length; i += 1) {
        const row: IInternalCrosstabDataRow = {
            cells: [],
            totalRow: !!rows[i].nodes.find((node) => (!!node && node.isTotal)),
            lastRowValue: false,
            key: rows[i].index
        };

        data.push(row);

        const datarow = datasetAggregations[rows[i].index];

        if (datarow) {
            for (let j = 0; j < columns.length; j += 1) {
                const column = columns[j];
                const cell = {
                    loading: typeof datarow[column.index] === 'undefined',
                    label: datarow[column.index],
                    isTotal: !!column.nodes.find((node) => (!!node && node.isTotal)),
                    key: column.index
                };
                row.cells.push(cell);
            }
        } else {
            for (let j = 0; j < columns.length; j += 1) {
                row.cells.push({
                    loading: true,
                    label: '',
                    isTotal: false,
                    key: j
                });
            }
        }
    }

    return data;
}

function findExpansionsForAxis(summaryItem: any, dataItem: any) {
    const exps: IMultiAxisDataServiceDataLayoutMemberExpansion[] = [];
    summaryItem.expansions.forEach((expansion: IMultiAxisDataServiceDataLayoutMemberExpansion) => {
        const nextLevel = summaryItem.items[expansion.path.length + 1];
        let addExpansions = false;

        if (nextLevel) {
            if (nextLevel.measuresLevel) {
                const nextNextLevel = summaryItem.items[expansion.path.length + 2];
                addExpansions = nextNextLevel && nextNextLevel.excludeSummary;
            } else {
                addExpansions = nextLevel.excludeSummary;
            }
        }

        if (addExpansions) {
            const node = getMultiAxisDatasetNode(dataItem, expansion.path).pop();
            if (node) {
                node.children.forEach((childNode) => {
                    if (!childNode.expanded) {
                        exps.push({
                            expanded: true,
                            path: expansion.path.concat([childNode.label])
                        })
                    }
                });
            }
        }
    });
    return exps;
}

class CrosstabVisualization extends React.Component<ICrosstabVisualization, never> {
    crosstabRef: any;

    hyperlinksRegister: IComponentsRefsRegister;

    constructor(a: any, b: any) {
        super(a, b);

        this.hyperlinksRegister = {};
        this.hyperlinksCollector = this.hyperlinksCollector.bind(this);
        this.crosstabRef = React.createRef();
        this.setExpandedStateForMember = this.setExpandedStateForMember.bind(this);
        this.setExpandedStateForLevel = this.setExpandedStateForLevel.bind(this);
    }

    componentDidMount() {
        this.callBeforeRender();
    }

    shouldComponentUpdate() {
        this.hyperlinksRegister = {};
        return true;
    }

    componentDidUpdate() {
        this.callBeforeRender();
    }

    setExpandedStateForLevel(level: any, expanded: boolean) {
        this.props.handle.updateLevelExpansion(level.id, expanded, {
            offsetTop: 0,
            offsetLeft: 0,
            positionOnNode: null
        });
    }

    setExpandedStateForMember(memberExpansion: IMultiAxisDataServiceDataLayoutMemberExpansion, isColumns: boolean, dynamicProps: any) {
        this.props.handle.updateMemberExpansion(memberExpansion, isColumns, dynamicProps);
    }

    actualizeOnScroll(param: OnScrollParameter) {
        const {
            scrollTop, scrollLeft, viewPortHeight, viewPortWidth, renderedHeight, renderedWidth
        } = param;
        const { offsetTop, offsetLeft } = this.props.dynamicProperties;

        if (scrollTop + viewPortHeight > offsetTop + renderedHeight) {
            const newOffset = Math.max(0, scrollTop - viewPortHeight);
            if (newOffset !== offsetTop) {
                this.props.handle.updateDynamicProperties({
                    offsetTop: newOffset,
                    positionOnNode: null
                });
            }
        }

        if (scrollTop <= offsetTop) {
            const newOffset = Math.max(0, scrollTop - viewPortHeight * 2);
            if (newOffset !== offsetTop) {
                this.props.handle.updateDynamicProperties({
                    offsetTop: newOffset,
                    positionOnNode: null
                });
            }
        }

        if (scrollLeft + viewPortWidth > offsetLeft + renderedWidth) {
            const newOffset = Math.max(0, scrollLeft - viewPortWidth);
            if (newOffset !== offsetLeft) {
                this.props.handle.updateDynamicProperties({
                    offsetLeft: newOffset,
                    positionOnNode: null
                });
            }
        }

        if (scrollLeft <= offsetLeft) {
            const newOffset = Math.max(0, scrollLeft - viewPortWidth * 2);
            if (newOffset !== offsetLeft) {
                this.props.handle.updateDynamicProperties({
                    offsetLeft: newOffset,
                    positionOnNode: null
                });
            }
        }
    }

    callBeforeRender() {
        const { linkOptions } = this.props;
        if (!linkOptions) {
            return;
        }
        const { beforeRender } = linkOptions;
        if (!beforeRender) {
            return;
        }

        const order = ['rows', 'columns', 'body'];
        let allPairs: ILinkToElemPair[] = [];

        _.each(order, (type) => {
            const refObjects = this.hyperlinksRegister[type];
            if (!refObjects) {
                return;
            }

            const pairs = _.map(refObjects, (refObject: ILinkToRefObjectPair) => {
                return {
                    data: refObject.data,
                    element: refObject.ref.current
                };
            });
            allPairs = allPairs.concat(pairs);
        });

        beforeRender(allPairs);
    }

    hyperlinksCollector(componentType: string, reactRefs: ILinkToRefObjectPair[]) {
        const { beforeRender } = this.props.linkOptions;
        if (!beforeRender) {
            return;
        }

        this.hyperlinksRegister[componentType] = reactRefs;
    }

    readDataset(offsetTop: number, offsetLeft: number): IInternalCrosstabData {
        const {
            dataset: {
                data: {
                    columns: datasetColumns,
                    rows: datasetRows,
                    aggregations: datasetAggregations
                },
                summary: datasetSummary
            },
            dynamicProperties: {
                crosstabWidth, crosstabHeight, xPointX, xPointY, minimalCellWidth, minimalCellHeight,
                bodyWidths
            },
            staticProperties: {
                levelsStructure
            },
            handle
        } = this.props;

        const { array: columns, visitedSize: knownWidth, missingIndex: missingIndexCols } = readHeader(datasetColumns, levelsStructure.columns,
            offsetLeft, // from
            Math.max(200, offsetLeft + (crosstabWidth - xPointX) * 3), // to
            datasetSummary.columns,
            datasetSummary.empty,
            // TODO derive from selected layout manager
            (nodeIndex: number) => (bodyWidths[nodeIndex] || minimalCellWidth));

        const { array: rows, visitedSize: knownHeight, missingIndex: missingIndexRows } = readHeader(datasetRows, levelsStructure.rows,
            offsetTop, // from
            Math.max(200, offsetTop + Math.abs(crosstabHeight - xPointY) * 3), // to
            datasetSummary.rows,
            datasetSummary.empty,
            // TODO derive from selected layout manager
            (nodeIndex: number) => (minimalCellHeight));

        const data = readAggregations(datasetAggregations, columns, rows);

        if (missingIndexCols >= 0 || missingIndexRows >= 0) {
            handle.requestDatasetUpdate(datasetSummary as any, Math.max(missingIndexRows - 1, 0), Math.max(missingIndexCols - 1, 0), 100, 100);
        } else {
            let top = -1;
            let left = -1;

            for (let i = 0; i < data.length && top < 0; i += 1) {
                for (let j = 0; j < data[i].cells.length; j += 1) {
                    if (data[i].cells[j].loading) {
                        top = i;
                    }
                }
            }

            if (data[0]) {
                for (let j = 0; j < data[0].cells.length && left < 0; j += 1) {
                    for (let i = 0; i < data.length; i += 1) {
                        if (data[i].cells[j].loading) {
                            left = j;
                        }
                    }
                }
            }

            if (left !== -1 || top !== -1) {
                const hasAllSummaries = [...levelsStructure.rows, ...levelsStructure.columns]
                    .filter((level) => (level.kind === 'level'))
                    .reduce((memo, level: any) => (memo && level.includeAll), true);

                if (hasAllSummaries) {
                    handle.requestDatasetUpdate(datasetSummary as any, rows[top].offset, columns[left].offset, 100, 100);
                } else {
                    handle.requestDatasetUpdate(datasetSummary as any, 0, 0, 0, 0);
                }
            }
        }

        return {
            rows, columns, data, knownWidth, knownHeight
        };
    }

    private expandLevelsWithRemovedSummaries(items: any[]) {
        let i = -1;

        while (items[i + 1] && items[i + 1].excludeSummary && !items[i + 1].expanded) {
            i += 1;
        }

        if (i !== -1) {
            if (items[i].measuresLevel) {
                i -= 1;
            }
            if (i >= 0) {
                this.props.handle.updateLevelExpansion(items[i].id, true);
            }
        }
    }

    private expandMembersWithRemovedSummaries(dataset: IMultiAxisDataset) {
        const {
            summary: {
                columns: summaryColumns,
                rows: summaryRows
            },
            data: {
                columns: dataColumns,
                rows: dataRows
            }
        } = dataset;

        const rowExpansions = findExpansionsForAxis(summaryRows, dataRows);
        const colExpansions = findExpansionsForAxis(summaryColumns, dataColumns);

        if (rowExpansions.length || colExpansions.length) {
            _.defer(() => {
                if (this.props.handle.updateMemberExpansions) {
                    this.props.handle.updateMemberExpansions(rowExpansions, colExpansions);
                }
            });
        }
    }

    // @ts-ignore
    private expandFirstLevel(items: any[]) {
        if (items[0] && !items[0].expanded) {
            this.props.handle.updateLevelExpansion(items[0].id, true);
        }
    }

    render() {
        const {
            dataset, handle, staticProperties, dynamicProperties, linkOptions
        } = this.props;

        let {
            offsetTop, offsetLeft
        } = dynamicProperties;
        const {
            bodyWidths, minimalCellWidth, minimalCellHeight,
            crosstabWidth, crosstabHeight, xPointX, xPointY
        } = dynamicProperties;
        let res;

        // this.expandFirstLevel(dataset.summary.rows.items); // TODO discuss this functionality
        // this.expandFirstLevel(dataset.summary.columns.items); // TODO discuss this functionality

        this.expandLevelsWithRemovedSummaries(dataset.summary.rows.items);
        this.expandLevelsWithRemovedSummaries(dataset.summary.columns.items);

        this.expandMembersWithRemovedSummaries(dataset);

        if (dynamicProperties.xPointX === -1 && dynamicProperties.xPointY === -1) {
            res = (
                <CrosstabProbeLayoutManager
                    ref={this.crosstabRef}
                    dataset={dataset}
                    handle={handle}
                    staticProperties={staticProperties}
                    dynamicProperties={dynamicProperties}
                />
            )
        } else {
            if (offsetLeft === -1 && dynamicProperties.positionOnNode && dynamicProperties.positionOnNode.columns) {
                offsetLeft = visitMultiAxisDatasetNodeNoTotals(dataset.data.columns, positionVisitor, {
                    offset: 0,
                    sizeSum: 0,
                    nodePosition: 0,
                    sizeQueue: [],
                    viewportSize: crosstabWidth - xPointX,
                    preViewportSize: crosstabWidth - xPointX,
                    postViewportSize: crosstabWidth - xPointX,
                    viewportOffset: dynamicProperties.positionOnNode.columns.viewPortOffset,
                    nodeIndex: dynamicProperties.positionOnNode.columns.nodeReference.index,
                    done: false,
                    // TODO derive from selected layout manager
                    sizeResolver: (nodeIndex: number) => (bodyWidths[nodeIndex] || minimalCellWidth)
                }, staticProperties.levelsStructure.columns.map((level) => ((level as IAxisItem).includeAll))).offset;

                // saves one Redux roundtrip
                this.props.dynamicProperties.offsetLeft = offsetLeft;
            }

            if (offsetTop === -1 && dynamicProperties.positionOnNode && dynamicProperties.positionOnNode.rows) {
                offsetTop = visitMultiAxisDatasetNodeNoTotals(dataset.data.rows, positionVisitor, {
                    offset: 0,
                    sizeSum: 0,
                    nodePosition: 0,
                    sizeQueue: [],
                    viewportSize: crosstabHeight - xPointY,
                    preViewportSize: crosstabHeight - xPointY,
                    postViewportSize: crosstabHeight - xPointY,
                    viewportOffset: dynamicProperties.positionOnNode.rows.viewPortOffset,
                    nodeIndex: dynamicProperties.positionOnNode.rows.nodeReference.index,
                    done: false,
                    // TODO derive from selected layout manager
                    sizeResolver: () => (minimalCellHeight)
                }, staticProperties.levelsStructure.rows.map((level) => ((level as IAxisItem).includeAll))).offset;

                // saves one Redux roundtrip
                this.props.dynamicProperties.offsetTop = offsetTop;
            }

            const preparedData: IInternalCrosstabData = this.readDataset(offsetTop, offsetLeft);

            res = (
                <CrosstabAdaptiveSizeLayoutManager
                    ref={this.crosstabRef}
                    preparedData={preparedData}
                    dataset={dataset}
                    handle={handle}
                    staticProperties={staticProperties}
                    dynamicProperties={dynamicProperties}
                    onScroll={(param: OnScrollParameter) => {
                        this.actualizeOnScroll(param)
                    }}
                    onLevelExpansionChange={this.setExpandedStateForLevel}
                    onMemberExpansionChange={this.setExpandedStateForMember}
                    linkOptions={linkOptions}
                    hyperlinksCollector={this.hyperlinksCollector}
                />
            )
        }

        return res;
    }
}

export default {
    legacyAdhoc: 'N/A',
    parentClass: 'Grid',
    cssClass: 'Crosstab',
    bundleName: 'adhoc.visualisation.chooser.type.CROSSTAB',
    name: 'Crosstab',
    group: 'adhoc.visualisation.chooser.type.GROUP_DATA_GRID',
    datasetType: datasetTypes.MULTI_AXIS,
    controlsDataLoading: true,
    component: CrosstabVisualization,
    staticPropertiesReducer: crosstabVisualizationStaticPropertiesReducer,
    defaultDynamicProperties: {
        offsetTop: -1,
        offsetLeft: -1,
        xPointX: -1,
        xPointY: -1,
        crosstabWidth: -1,
        crosstabHeight: -1,
        minimalCellWidth: -1,
        minimalCellHeight: -1,

        centralBorderWidth: 0,

        headerWidths: [],
        bodyWidths: []
    },
    requirements: {
        measures: {
            min: 1
        },
        fields: {
            min: 0
        }
    }
};
